import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, ShoppingCart, Leaf, ShieldCheck, Sprout, Users } from "lucide-react";
import { HomeNewHero } from "@/components/HomeNewHero";
import { Stars } from "@/components/StarRating";
import { imgSrc } from "@/lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIMG = (slug) => `/images/categories-landing/${slug}/product.jpg`;

const TRUST = [
  { icon: Leaf, label: "100% Natural" },
  { icon: ShieldCheck, label: "No Artificial Additives" },
  { icon: Sprout, label: "Sustainably Sourced" },
  { icon: Users, label: "Trusted by Families Across India" },
  { label: "If it's from Nutribest, I don't need to worry about quality." },
  { label: "Grown with care. Delivered with honesty." },
  { label: "Every family deserves food they never have to question." },
  { label: "Pure Nutrition. Real Superfoods." },
  { label: "Earned Trust." },
];

const NEW_ARRIVALS = [
  { name: "Moringa Leaf Powder", rating: 4.8, count: 180, price: 449, badge: "New", badgeColor: "#2E8B57", img: PIMG("moringa-greens") },
  { name: "Golden Turmeric Latte", rating: 4.7, count: 96, price: 549, badge: "New", badgeColor: "#2E8B57", img: PIMG("wellness-essentials") },
  { name: "Chia & Flax Mix", rating: 4.6, count: 120, price: 399, badge: "New", badgeColor: "#2E8B57", img: PIMG("seeds-nuts") },
  { name: "Kids Daily Protein", rating: 4.8, count: 142, price: 499, badge: "New", badgeColor: "#2E8B57", img: PIMG("kids-nutrition") },
];

const TRENDING = [
  { name: "Ashwagandha Root", rating: 4.7, count: 410, price: 499, badge: "Trending", badgeColor: "#D64B6A", img: PIMG("proteins-plant-powders") },
  { name: "Daily Superfood Blend", rating: 4.7, count: 320, price: 599, badge: "Trending", badgeColor: "#D64B6A", img: PIMG("superfood-blends") },
  { name: "Mango Ginger Pickle", rating: 4.5, count: 210, price: 299, badge: "Trending", badgeColor: "#D64B6A", img: PIMG("pickles-chutneys") },
  { name: "Wellness Latte Mix", rating: 4.6, count: 150, price: 549, badge: "Trending", badgeColor: "#D64B6A", img: PIMG("wellness-essentials") },
];

const BEST_SELLERS = [
  { name: "Spirulina Powder", rating: 4.8, count: 980, price: 549, badge: "Best Seller", badgeColor: "#E39A2B", img: PIMG("moringa-greens") },
  { name: "Plant Protein Powder", rating: 4.6, count: 690, price: 699, badge: "Best Seller", badgeColor: "#E39A2B", img: PIMG("proteins-plant-powders") },
  { name: "Mixed Seeds & Nuts", rating: 4.7, count: 540, price: 399, badge: "Best Seller", badgeColor: "#E39A2B", img: PIMG("seeds-nuts") },
  { name: "Festive Gift Hamper", rating: 4.9, count: 320, price: 1299, badge: "Best Seller", badgeColor: "#E39A2B", img: PIMG("gifting") },
];

const ALL_PRODUCTS = [
  { name: "Moringa Leaf Powder", cat: "Moringa", rating: 4.8, count: 260, price: 449, img: PIMG("moringa-greens") },
  { name: "Ashwagandha Root", cat: "Adaptogens", rating: 4.7, count: 410, price: 499, img: PIMG("proteins-plant-powders") },
  { name: "Mixed Seeds & Nuts", cat: "Seeds & Nuts", rating: 4.6, count: 180, price: 399, img: PIMG("seeds-nuts") },
  { name: "Plant Protein Powder", cat: "Protein", rating: 4.6, count: 290, price: 699, img: PIMG("proteins-plant-powders") },
  { name: "Spirulina Powder", cat: "Moringa", rating: 4.6, count: 392, price: 549, img: PIMG("moringa-greens") },
  { name: "Wellness Latte Mix", cat: "Wellness", rating: 4.5, count: 150, price: 549, img: PIMG("wellness-essentials") },
  { name: "Chia Seeds", cat: "Seeds & Nuts", rating: 4.7, count: 220, price: 349, img: PIMG("seeds-nuts") },
  { name: "Daily Superfood Blend", cat: "Wellness", rating: 4.7, count: 320, price: 599, img: PIMG("superfood-blends") },
];

const TABS = ["All", "Moringa", "Adaptogens", "Seeds & Nuts", "Protein", "Wellness"];

const TESTIMONIALS = [
  { quote: "The moringa powder has become part of our family's daily routine. Pure, fresh and genuinely trustworthy.", name: "Ananya Rao", place: "Bengaluru" },
  { quote: "Finally a brand that is honest about what goes in. My kids love the nutrition mix and I love the quality.", name: "Rohit Mehta", place: "Pune" },
  { quote: "Clean ingredients, beautiful packaging and it actually works. Nutribest has earned a permanent spot in my pantry.", name: "Shweta Nair", place: "Mumbai" },
];

const addDemo = (name) => toast.success(`${name} added to cart (demo)`);

const ProductCard = ({ p, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-forest/5 shadow-sm p-3 relative">
    {p.badge && (
      <span className="absolute top-4 left-4 z-10 text-[9px] font-bold uppercase tracking-wide text-white px-2.5 py-1 rounded-full" style={{ backgroundColor: p.badgeColor }}>{p.badge}</span>
    )}
    <div className="rounded-xl overflow-hidden bg-[#FBF7EC] aspect-square"><img src={p.img} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>
    <h3 className="font-semibold text-forest text-sm mt-3 leading-tight">{p.name}</h3>
    <div className="flex items-center gap-1.5 mt-1"><Stars value={p.rating} size={11} /><span className="text-[10px] text-mutedgreen">{p.rating} ({p.count})</span></div>
    <div className="flex items-center justify-between mt-2">
      <p className="text-forest font-bold">₹ {p.price}</p>
      <button onClick={() => addDemo(p.name)} data-testid={`${testid}-add`} aria-label="Add to cart" className="w-9 h-9 rounded-full bg-forest hover:bg-gold text-cream flex items-center justify-center transition-colors"><ShoppingCart size={15} /></button>
    </div>
  </div>
);

const ProductRow = ({ id, eyebrow, title, sub, items, viewAllTo = "/collection" }) => (
  <section className="container-nb py-14 sm:py-16" data-testid={`${id}-section`}>
    <div className="relative text-center mb-8">
      <p className="eyebrow !text-gold mb-2">{eyebrow}</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-forest">{title}</h2>
      {sub && <p className="text-mutedgreen text-sm mt-2">{sub}</p>}
      <Link to={viewAllTo} data-testid={`${id}-view-all`} className="hidden sm:inline-flex items-center gap-1 absolute right-0 top-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-forest/60 hover:text-forest transition-colors">View All <ArrowRight size={13} /></Link>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      {items.map((p, i) => <ProductCard key={i} p={p} testid={`${id}-card-${i}`} />)}
    </div>
    <div className="sm:hidden mt-6 text-center">
      <Link to={viewAllTo} data-testid={`${id}-view-all-mobile`} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-forest/70 hover:text-forest transition-colors">View All <ArrowRight size={13} /></Link>
    </div>
  </section>
);

const CategoryStrip = () => {
  const scroller = useRef(null);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    axios.get(`${API}/categories`).then(({ data }) => setCats(data || [])).catch(() => {});
  }, []);
  const scrollBy = (dir) => scroller.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  return (
    <section className="container-nb py-14 sm:py-16" data-testid="category-strip-section">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow !text-gold mb-2">Shop By</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-forest">Category</h2>
          <p className="text-mutedgreen text-sm mt-1">Wholesome superfoods for every stage of your journey.</p>
        </div>
        <div className="hidden sm:flex items-center gap-4">
          <Link to="/shop-by-category" data-testid="category-strip-view-all" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-forest/60 hover:text-forest transition-colors">View All <ArrowRight size={13} /></Link>
          <div className="flex gap-2">
            <button onClick={() => scrollBy(-1)} data-testid="category-strip-prev" aria-label="Scroll left" className="w-10 h-10 rounded-full border border-forest/20 text-forest flex items-center justify-center hover:bg-forest hover:text-cream transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => scrollBy(1)} data-testid="category-strip-next" aria-label="Scroll right" className="w-10 h-10 rounded-full border border-forest/20 text-forest flex items-center justify-center hover:bg-forest hover:text-cream transition-colors"><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>
      <div ref={scroller} className="flex gap-5 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {cats.map((cat, i) => (
          <motion.div
            key={cat.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="shrink-0 w-72"
          >
            <Link
              to={`/category/${cat.slug}`}
              data-testid={`category-strip-card-${cat.slug}`}
              style={{ backgroundColor: cat.color_bg }}
              className="group relative block overflow-hidden rounded-3xl min-h-[320px]"
            >
              {cat.image && (
                <img src={imgSrc(cat.image)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              )}
              <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${cat.color_bg} 18%, ${cat.color_bg}cc 34%, transparent 62%)` }} />
              <div className="relative z-10 p-7 max-w-[70%]">
                <span className="font-serif leading-none text-4xl sm:text-5xl" style={{ color: cat.color_accent }}>{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-xl sm:text-2xl mt-3 leading-tight" style={{ color: cat.color_accent }}>{cat.name}</h3>
                {cat.description && <p className="text-sm mt-2 text-ink/70 leading-relaxed">{cat.description}</p>}
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border mt-6 transition-colors bg-white/40" style={{ borderColor: cat.color_accent, color: cat.color_accent }}>
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const AllProductsGrid = () => {
  const [tab, setTab] = useState("All");
  const items = tab === "All" ? ALL_PRODUCTS : ALL_PRODUCTS.filter((p) => p.cat === tab);
  return (
    <section className="container-nb py-16 sm:py-20" data-testid="all-products-section">
      <div className="relative text-center mb-8">
        <p className="eyebrow !text-gold mb-2">Everything In One Place</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-forest">All Products</h2>
        <Link to="/collection" data-testid="all-products-view-all" className="hidden sm:inline-flex items-center gap-1 absolute right-0 top-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-forest/60 hover:text-forest transition-colors">View All <ArrowRight size={13} /></Link>
      </div>
      <div className="flex flex-wrap justify-center gap-2.5 mb-10">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`all-products-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`text-[11px] font-semibold uppercase tracking-[0.12em] px-5 py-2.5 rounded-full border transition-colors ${tab === t ? "bg-forest text-cream border-forest" : "bg-white text-forest/70 border-forest/15 hover:border-forest/40 hover:text-forest"}`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {items.map((p, i) => <ProductCard key={`${tab}-${i}`} p={p} testid={`all-products-card-${i}`} />)}
      </div>
      <div className="sm:hidden mt-6 text-center">
        <Link to="/collection" data-testid="all-products-view-all-mobile" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-forest/70 hover:text-forest transition-colors">View All <ArrowRight size={13} /></Link>
      </div>
    </section>
  );
};

export default function HomeNew() {
  return (
    <div data-testid="home-new-page" className="bg-cream">
      {/* 1. Hero Carousel (home-new only) */}
      <HomeNewHero />

      {/* 2. Trust Bar */}
      <section className="bg-cream border-y border-forest/10 overflow-hidden" data-testid="trust-bar-section">
        <div className="py-5">
          <div className="group flex overflow-hidden" data-testid="trust-marquee">
            {[0, 1].map((dup) => (
              <div
                key={dup}
                aria-hidden={dup === 1}
                className="animate-marquee flex shrink-0 items-center"
              >
                {TRUST.map(({ icon: Icon, label }, i) => (
                  <div key={`${dup}-${i}`} className="flex items-center shrink-0">
                    <div className="flex items-center gap-3 px-8">
                      {Icon && (
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-forest shadow-sm shrink-0"><Icon size={18} /></span>
                      )}
                      <span className="text-sm font-semibold text-forest leading-tight whitespace-nowrap">{label}</span>
                    </div>
                    <span className="text-forest/30 select-none">•</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Shop by Category Strip */}
      <CategoryStrip />

      {/* 4. New Arrivals */}
      <ProductRow id="new-arrivals" eyebrow="Fresh From Nature" title="New Arrivals" sub="Just landed in our wellness family." items={NEW_ARRIVALS} />

      {/* 5. Trending Now */}
      <div className="bg-[#F4F8EE]">
        <ProductRow id="trending" eyebrow="What Everyone Loves" title="Trending Now" sub="Picks flying off our shelves." items={TRENDING} />
      </div>

      {/* 6. Best Sellers */}
      <ProductRow id="best-sellers" eyebrow="Our Popular Picks" title="Best Sellers" sub="Loved by many. Trusted for good." items={BEST_SELLERS} />

      {/* 7. Featured Category Banner */}
      <section className="relative overflow-hidden" data-testid="featured-category-banner">
        <img src="/images/categories-landing/moringa-greens/hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #DDE9C3 12%, #DDE9C3cc 38%, transparent 70%)" }} />
        <div className="relative container-nb py-20 sm:py-28">
          <div className="max-w-md">
            <p className="eyebrow !text-forest/70 mb-3">Featured Category</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest leading-tight">Start with Moringa</h2>
            <p className="text-ink/70 mt-4 leading-relaxed">Nature's powerhouse for everyday nutrition — rich in vitamins, minerals and plant-based goodness.</p>
            <Link to="/category/moringa-greens" data-testid="featured-banner-shop-now" className="inline-flex items-center gap-2 bg-forest hover:bg-gold text-cream text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 rounded-full mt-7 transition-colors">
              Shop Now <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* 8. All Products Grid */}
      <AllProductsGrid />

      {/* 9. Testimonials */}
      <section className="bg-cream border-t border-forest/10" data-testid="testimonials-section">
        <div className="container-nb py-16 sm:py-20">
          <div className="text-center mb-10">
            <p className="eyebrow !text-gold mb-2">Kind Words</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest">Loved by Families</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="bg-white rounded-3xl border border-forest/10 p-8 shadow-sm">
                <div className="mb-4"><Stars value={5} size={14} /></div>
                <p className="font-serif text-lg text-forest leading-relaxed">“{t.quote}”</p>
                <div className="mt-6 pt-5 border-t border-forest/10">
                  <p className="font-semibold text-forest text-sm">{t.name}</p>
                  <p className="text-mutedgreen text-xs mt-0.5">{t.place}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
